import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@material-ui/core';
import { orderBy } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { KeywordSortByTypeGq } from '../../../../graphql/projects/models/KeywordSortByTypeGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import { adjustTextColor } from '../../Palette/adjustTextColor';

export interface RankingHeatMapProps extends ChartProps {}

export const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      marginTop: theme.spacing(4),
    },
    tableCellRank: {
      borderWidth: 1,
      borderColor: 'white',
      borderStyle: 'solid',
      padding: theme.spacing(1),
    },
    tableCell: {
      padding: theme.spacing(1),
    },
    tableCellText: {
      fontWeight: 500,
      opacity: 1,
      fontSize: '12px',
    },
  })
);

const RankingHeatMap: React.FC<RankingHeatMapProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showDomainIds: true,
          showTagGroupId: true,
          showTagId: true,
          showLimitItems: true,
        },
      })),
    []
  );

  // Fetch keyword data
  const selectedTagGroup = project?.tagGroups?.find(
    (tg) => tg.id === analysisFilterState?.tagGroupId
  );
  const selectedTag = project?.tagGroups
    ?.find((tg) => tg.id === analysisFilterState?.tagGroupId)
    ?.tags.find((t) => t.id === analysisFilterState?.tagId);

  const orderedDomains = orderBy(project.domains, [
    (domain) => domain.type !== DomainTypeGq.PROJECT,
    (domain) => analysisFilterState?.domainIds.indexOf(domain.id),
    (domain) => domain.name,
  ]).filter((domain) => analysisFilterState?.domainIds.includes(domain.id));

  const keywordsQuery = projectsApiService.queries.useGetKeywords({
    keywordQuery: {
      filtering: {
        projectId: project.id,
        domainRankFilters: [
          {
            minRank: 0,
            domainId: project.domains[0]?.id,
          },
        ],
        archived: false,
        tagFilters: selectedTagGroup?.tags.map((tag) => ({
          tagGroupId: tag.tagGroupId,
          tagId: selectedTag?.id ?? tag.id,
        })),
      },
      sorting: {
        sortByType: KeywordSortByTypeGq.VOLUME,
        sortDirection: 'desc',
      },
      paging: {
        page: 0,
        amountPerPage: analysisFilterState?.limitItems,
      },
    },
    domainIds: orderedDomains.map((d) => d.id),
  });

  //Styling
  const classes = useClasses();
  const getColorIndex = (
    rank: number,
    rankStart: number,
    rankEnd: number,
    indexStart: number,
    indexEnd: number
  ) => {
    const coefficient = (rank - rankStart) / (rankEnd - rankStart);
    return Math.round(coefficient * (indexEnd - indexStart) + indexStart);
  };
  const textColor = adjustTextColor(palette, 0.85, 0);

  // Render
  if (!keywordsQuery.keywords.length && keywordsQuery.query.loading) {
    return <AnalysisChartLoading />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <Table size="small" className={classes.table} padding="none">
        <colgroup>
          <col width="180px" />
          <col width="120px" />
          {selectedTagGroup && <col width="140px" />}
          {orderedDomains.map((d) => (
            <col width={'100px'} key={d.id} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableCell}>
              {internationalText.chart.competitive_heatmap.keyword[language]}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {internationalText.chart.competitive_heatmap.volume[language]}
            </TableCell>
            {selectedTagGroup && <TableCell>{selectedTagGroup.name}</TableCell>}
            {orderedDomains.map((domain) => (
              <TableCell className={classes.tableCell} align="center" key={domain.id}>
                {domain.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {keywordsQuery.keywords.map((kw) => (
            <TableRow key={kw.id}>
              <TableCell className={classes.tableCell}>{kw.keyword}</TableCell>
              <TableCell className={classes.tableCell}>{formatNumber(kw.volume, 0)}</TableCell>
              {selectedTagGroup && (
                <TableCell>
                  {selectedTagGroup?.tags?.find((tg) => kw.tags.includes(tg.id))?.name}
                </TableCell>
              )}
              {orderedDomains.map((domain) => {
                const rank = kw?.domainRanks?.find((dr) => dr.domainId === domain.id)?.rank;
                let color = '';
                if (rank >= 1 && rank <= 3) color = palette.performanceScale[0];
                else if (rank >= 4 && rank <= 10) color = palette.performanceScale[1];
                else if (rank >= 11 && rank <= 20) color = palette.performanceScale[2];
                else if (rank >= 21 && rank <= 50) color = palette.performanceScale[3];
                else if (rank >= 51 && rank <= 100) color = palette.performanceScale[4];
                else color = palette.performanceScale[5];

                return (
                  <TableCell
                    key={domain.id}
                    align="center"
                    style={{ backgroundColor: color, color: textColor }}
                    className={classes.tableCellRank}
                  >
                    <span className={classes.tableCellText}>{rank ?? '100+'}</span>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RankingHeatMap;
